import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LoginRequestDTO, ChangePasswordRequestDTO } from '../interfaces/AuthInterface';
import { login, changePassword } from '../services/AuthService';
import SessionService from '../services/SessionService';
import './LoginScreen.css';
import logo from '../assets/logo.png';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { SharedModal } from '../components/SharedModal';
import { homePath } from '../utils/homePath';
import { landingPath } from '../utils/landingPath';

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'changePassword'>('login');
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingChangePassword, setLoadingChangePassword] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.disconnected) {
      setIsModalVisible(true);
    }
  }, [location.state]);

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(value);
    setIsEmailValid(isValid);
    setIsFormValid(isValid && value.trim() !== '' && password.trim() !== '');
  };

  const validatePassword = (value: string) => {
    setIsPasswordValid(value.length >= 1);
    setIsFormValid(email.trim() !== '' && value.trim() !== '');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoadingLogin(true);
    setErrorMessage('');

    try {
      const requestData: LoginRequestDTO = { email, password };
      const response = await login(requestData);

      if (response.mustChangePassword) {
        setMustChangePassword(true);
        setTempToken(response.token);
        setActiveTab('changePassword');
      } else if (response.subscriptionPending) {
        navigate('/subscription');
      } else {
        navigate(await landingPath(response.isFirstAccess));
      }
    } catch (error: any) {
      console.error('Error logging in:', error);
      setIsPasswordValid(false);
      setErrorMessage(error.message || 'Não foi possível entrar');
    } finally {
      setLoadingLogin(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setErrorMessage('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoadingChangePassword(true);
    setErrorMessage('');

    try {
      const requestData: ChangePasswordRequestDTO = {
        email,
        currentPassword: password,
        newPassword: newPassword
      };
      await changePassword(requestData);
      // Troca de senha obrigatória = primeiro acesso: segue para o assistente quando a conta ainda não está pronta.
      navigate(await landingPath(true));
    } catch (error: any) {
      console.error('Error changing password:', error);
      setErrorMessage(error.message || 'Falha ao alterar senha');
    } finally {
      setLoadingChangePassword(false);
    }
  };

  useEffect(() => {
    const token = SessionService.getToken();
    if (token) {
      const isPending = SessionService.getSession('@app/subscription_pending');
      navigate(isPending ? '/subscription' : homePath());
    }
  }, [navigate]);

  return (
    <div className="login-screen-wrapper">
      {isModalVisible && (
        <SharedModal
          open={isModalVisible}
          onClose={handleCloseModal}
          title="Sessão expirada"
          size="sm"
          footer={
            <button
              type="button"
              className="shared-modal__btn shared-modal__btn--primary"
              onClick={handleCloseModal}
            >
              Continuar
            </button>
          }
        >
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--primary-color)' }}>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <p className="shared-modal__text">Sua sessão expirou. Entre novamente.</p>
        </SharedModal>
      )}

      <div className="login-split">
        <aside className="login-brand-panel" aria-label="MENDS">
          <div className="login-brand-content">
            <img src={logo} alt="Logo da MENDS" className="login-brand-logo" />
            <h2 className="login-brand-title">Bem-vindo à MENDS</h2>
            <p className="login-brand-support">Conversas que fecham negócio.</p>
          </div>
        </aside>

        <div className="login-form-panel">
          <div className="login-form-inner">
            <div className="login-header">
              <h1><span>{mustChangePassword ? 'Trocar senha' : 'Bem-vindo'}</span></h1>
              <p><span>{mustChangePassword ? 'Crie uma nova senha para continuar' : 'Entre com seu e-mail e senha'}</span></p>
            </div>

            {activeTab === 'login' ? (
              <form className="login-form" onSubmit={handleLogin}>
                <div className="login-form-group">
                  <label htmlFor="email"><span>E-mail</span></label>
                  <div className="login-input-wrapper">
                    <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        validateEmail(e.target.value);
                        setIsEmailValid(true);
                        setErrorMessage('');
                      }}
                      placeholder="Seu e-mail"
                      className={!isEmailValid ? 'error' : ''}
                    />
                  </div>
                </div>

                <div className="login-form-group">
                  <label htmlFor="password"><span>Senha</span></label>
                  <div className="login-input-wrapper">
                    <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        validatePassword(e.target.value);
                        setIsPasswordValid(true);
                        setErrorMessage('');
                      }}
                      placeholder="Sua senha"
                      className={!isPasswordValid ? 'error' : ''}
                    />
                  </div>
                </div>

                {errorMessage && (
                  <div className="login-error-banner">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{errorMessage}</span>
                  </div>
                )}

                <p className="login-acceptance-text">
                  Ao continuar, você aceita a{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">Política de Privacidade</Link>
                  {' '}e os{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">Termos de Uso</Link>.
                </p>

                <button
                  type="submit"
                  className="login-submit-button"
                  disabled={!isFormValid || loadingLogin}
                >
                  {loadingLogin ? (
                    <LoadingSpinner size="sm" color="white" />
                  ) : (
                    <>
                      <span>Entrar</span>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form className="login-form" onSubmit={handleChangePassword}>
                <div className="login-change-password-info">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <p><span>Você está usando uma senha temporária. Crie uma nova senha para a sua conta.</span></p>
                </div>

                <div className="login-form-group">
                  <label htmlFor="newPassword"><span>Nova senha</span></label>
                  <div className="login-input-wrapper">
                    <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setErrorMessage('');
                      }}
                      placeholder="Nova senha"
                      minLength={6}
                    />
                  </div>
                  <span className="login-hint"><span>Minimum 6 characters</span></span>
                </div>

                <div className="login-form-group">
                  <label htmlFor="confirmPassword"><span>Confirme a nova senha</span></label>
                  <div className="login-input-wrapper">
                    <svg className="login-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setErrorMessage('');
                      }}
                      placeholder="Repita a nova senha"
                    />
                  </div>
                </div>

                {errorMessage && (
                  <div className="login-error-banner">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{errorMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="login-submit-button"
                  disabled={!newPassword || !confirmPassword || loadingChangePassword}
                >
                  {loadingChangePassword ? (
                    <LoadingSpinner size="sm" color="white" />
                  ) : (
                    <>
                      <span>Trocar senha</span>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>

                {!mustChangePassword && (
                  <button
                    type="button"
                    className="login-back-button"
                    onClick={() => {
                      setActiveTab('login');
                      setNewPassword('');
                      setConfirmPassword('');
                      setErrorMessage('');
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span>Voltar ao login</span>
                  </button>
                )}
              </form>
            )}

            <div className="login-footer">
              <p>
                <Link to="/home" className="login-footer-link">Início</Link>
                {' · '}
                <Link to="/privacy" className="login-footer-link">Política de Privacidade</Link>
                {' · '}
                <Link to="/terms" className="login-footer-link">Termos de Uso</Link>
              </p>
              <p><span>© {new Date().getFullYear()} Mends. Todos os direitos reservados.</span></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
